import {
  Tooltip,
  type TooltipProps,
  Typography,
  type TypographyProps,
  type TypographyTypeMap,
  Zoom,
} from "@mui/material";
import { type ReactNode, forwardRef } from "react";
import { DetectOverflow } from "../index";

export interface ITypoTooltipProps extends TypographyProps<"div"> {
  isOverflowing?: boolean;
  TooltipProps?: Partial<TooltipProps>;
  tooltip: ReactNode;
}

export type TypoTooltipProps<
  T extends ReactNode,
  RootComponent extends React.ElementType = TypographyTypeMap["defaultComponent"],
  AdditionalProps = object,
> = TypographyProps<RootComponent, AdditionalProps> & {
  TooltipProps?: Partial<TooltipProps>;
  tooltip?: ReactNode;
  children: T;
};

const TypoTooltipDetectOverflow = forwardRef<HTMLDivElement, ITypoTooltipProps>(function TypoTooltipDetectOverflow(
  { isOverflowing = false, tooltip, children, TooltipProps, ...props }: ITypoTooltipProps,
  ref
) {
  if (isOverflowing) {
    return (
      <Tooltip {...TooltipProps} title={tooltip} arrow={TooltipProps?.arrow} slots={{
        transition: Zoom
      }}>
        <Typography component="div" {...props} ref={ref}>
          {children}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Typography component="div" {...props} ref={ref}>
      {children}
    </Typography>
  );
});

export const TypoTooltip = <
  T extends ReactNode,
  RootComponent extends React.ElementType = TypographyTypeMap["defaultComponent"],
  AdditionalProps = object,
>({
  children,
  tooltip,
  ...props
}: TypoTooltipProps<T, RootComponent, AdditionalProps>) => {
  return (
    <DetectOverflow childAsContainer>
      <TypoTooltipDetectOverflow tooltip={tooltip ?? children} {...props}>
        {children}
      </TypoTooltipDetectOverflow>
    </DetectOverflow>
  );
};
